import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useApi } from "boot/axios";
// import { useFCMStore } from "./fcm";
import { useRealtimeStore } from "./realtime.js";
import { LocalStorage } from "quasar";
import { ssrSafeLocalStorage } from "src/utils/ssrSafeStorage";
import SessionStorageService from "src/services/sessionStorageService";
import { useLogementsStore } from "stores/useLogementsStore";
import { useFavorisStore } from "stores/useFavorisStore";
import { backAdresse } from "boot/utils";
import { analyticsService } from "src/services/analyticsService";
import ky from "ky";

const { api } = useApi();

// Constante pour les champs utilisateur (évite la duplication)
const USER_FIELDS =
  "id,first_name,last_name,telephone,genre,avatar,status,telephone,email,role.id,role.name,certified,first_authentificate,solde,parrain,recto_piece,verso_piece,lieu_habitation,lieu_d_habitation.id,lieu_d_habitation.libelle,code_de_parrainage,liste_parrainage,accepted_terms,solde_en_attente";

/**
 * Store unifié pour l'authentification et la gestion utilisateur
 *
 * Ce store combine toutes les fonctionnalités de useAuthStore et useUserStore
 * pour éliminer la duplication et améliorer la cohérence.
 *
 * Fonctionnalités :
 * - Login classique et social
 * - Gestion des tokens et sessions
 * - Cache intelligent pour les données utilisateur
 * - Initialisation parallélisée des services (FCM, Realtime)
 * - Persistance automatique via LocalStorage
 * - Gestion des CGU et rôles
 */
export const useAuthUnifiedStore = defineStore(
  "authUnified",
  () => {
    // ===== STATE =====
    const user = ref(null);
    const token = ref(null);
    const refreshToken = ref(null);
    const isAuthenticated = ref(false);
    const isLoading = ref(false);
    const userRole = ref(null);
    const lastLogin = ref(null);
    const sessionToken = ref(null);

    // Cache intelligent pour éviter les requêtes répétitives
    const cache = ref({
      user: null,
      lastFetch: 0,
      ttl: 30000, // 30 secondes
    });

    // Statut des services
    const fcmInitialized = ref(false);
    const realtimeInitialized = ref(false);

    // Mapping des rôles pour la navigation
    const navigator = {
      Hôte: "hote",
      Voyageur: "voyageur",
      Démarcheur: "demarcheur",
    };

    // ===== GETTERS =====
    const isHost = computed(() => user.value?.role?.name === "Hôte");
    const isVoyageur = computed(() => user.value?.role?.name === "Voyageur");
    const isDemarcheur = computed(() => {
      const role = user.value?.role?.name;
      // Accepter "Démarcheur" ou "Démarcheurs"
      return role == "Démarcheur" || role === "Démarcheurs";
    });

    const userFullName = computed(() => {
      if (!user.value) return "";
      return `${user.value.first_name || ""} ${
        user.value.last_name || ""
      }`.trim();
    });

    const userEmail = computed(() => user.value?.email || "");
    const userPhone = computed(() => user.value?.telephone || "");
    const userAvatar = computed(() => user.value?.avatar || null);

    const hasValidSession = computed(() => {
      return isAuthenticated.value && user.value && token.value;
    });

    // ===== ACTIONS =====

    /**
     * ✅ NOUVEAU: Point d'entrée centralisé pour définir l'état d'authentification
     * @param {string} newToken - Le token d'accès EN CLAIR
     * @param {Object} userData - Les données de l'utilisateur
     * @param {boolean} initServices - Si true, initialise les services immédiatement (défaut: true)
     */
    function setAuthData(newToken, userData, initServices = true) {
      if (!newToken || !userData) {
        return;
      }

      // Mettre à jour l'état du store
      token.value = newToken;
      user.value = userData;
      isAuthenticated.value = true;
      userRole.value = userData?.role?.name || null;
      lastLogin.value = new Date().toISOString();
      sessionToken.value = newToken; // Garder une copie en clair pour la session actuelle

      // Configurer l'en-tête API
      api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

      // ✅ Utiliser SessionStorageService qui chiffre automatiquement le token
      SessionStorageService.setToken(newToken);
      SessionStorageService.setUser(userData);

      // Mettre à jour le cache
      cache.value.user = userData;
      cache.value.lastFetch = Date.now();

      try {
        analyticsService.setUserProperties(userData);
      } catch {
        /* analytics ne doit jamais faire échouer l’auth */
      }

      // ⚡ Initialiser les services en arrière-plan de manière vraiment non-bloquante
      if (initServices) {
        setTimeout(() => {
          initializeServices();
          void synchroniserMoyensPaiementRetrait(false);
        }, 0);
      }
    }

    /**
     * Charge et persiste les moyens de paiement retrait (`payout`).
     * @param {boolean} [force] — true après login explicite, false au boot (cache persisté).
     */
    async function synchroniserMoyensPaiementRetrait(force = false) {
      try {
        const { useExtrasStore } = await import("stores/useExtrasStore");
        await useExtrasStore().fetchPayoutMethods({ force });
      } catch {
        /* ne bloque pas l'auth */
      }
    }

    /**
     * Login classique avec email et mot de passe
     * Mode JSON par défaut : les tokens sont retournés dans le body de la réponse
     *
     * @param {string} email - Email de l'utilisateur
     * @param {string} password - Mot de passe
     */
    async function login(email, password) {
      try {
        isLoading.value = true;

        // ✅ S'assurer que le mode cookie est désactivé
        LocalStorage.remove("useCookieAuth");

        // 🔑 LOGIN JSON STANDARD : Directus retourne access_token et refresh_token dans le body
        const response = await api.post("/auth/login", {
          email,
          password,
          mode: "json", // ✅ Explicite : on veut les tokens dans le body JSON
        });

        const accessToken = response.data.data.access_token;
        const refreshTokenValue = response.data.data.refresh_token;

        SessionStorageService.setToken(accessToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        const userResponse = await api.get("users/me", {
          params: { fields: USER_FIELDS },
        });

        setAuthData(accessToken, userResponse.data.data);
        await synchroniserMoyensPaiementRetrait(true);

        // ✅ Sauvegarder le refresh token
        if (refreshTokenValue) {
          refreshToken.value = refreshTokenValue;
          SessionStorageService.setRefreshToken(refreshTokenValue);
        }
      } catch (error) {
        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    /**
     * Login social (Google, Apple, etc.)
     * @param {string} provider - Fournisseur OAuth
     * @param {string} role - Rôle sélectionné
     */
    async function socialLogin(provider, role) {
      try {
        isLoading.value = true;

        const response = await api.post("/auth/login", {
          provider,
          role,
        });

        const accessToken = response.data.data.access_token;
        const refreshTokenValue = response.data.data.refresh_token;

        SessionStorageService.setToken(accessToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        const userResponse = await api.get("users/me", {
          params: { fields: USER_FIELDS },
        });

        setAuthData(accessToken, userResponse.data.data);
        await synchroniserMoyensPaiementRetrait(true);

        // ✅ Sauvegarder le refresh token via SessionStorageService
        if (refreshTokenValue) {
          refreshToken.value = refreshTokenValue;
          SessionStorageService.setRefreshToken(refreshTokenValue);
        }
      } catch (error) {
        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    /**
     * Déconnexion complète avec nettoyage
     */
    async function logout() {
      try {
        // Mettre isAuthenticated à false IMMÉDIATEMENT
        isAuthenticated.value = false;

        // ✅ Vider les données authentifiées des autres stores
        const logementsStore = useLogementsStore();
        logementsStore.clearAuthData();
        const favorisStore = useFavorisStore();
        favorisStore.clearFavoris();

        // Nettoyer les services d'abord
        await cleanupServices();

        // ✅ Vérifier le type d'authentification et déconnecter en conséquence
        const isAppleAuth = LocalStorage.getItem("appleAuth");
        const isGoogleAuth = LocalStorage.getItem("googleAuth");
        const hadCookieAuth = LocalStorage.getItem("useCookieAuth");

        // Directus : invalider la session cookie (OAuth redirect, mode cookie-only, etc.)
        if (hadCookieAuth || isAppleAuth || isGoogleAuth) {
          try {
            await api.post(
              "/auth/logout",
              { mode: "cookie" },
              { withCredentials: true }
            );
          } catch (error) {
            // Pas de session cookie ou CORS : poursuivre le nettoyage local
          }
        }

        if (isAppleAuth) {
          try {
            const { appleAuthService } = await import(
              "../services/appleAuthService.js"
            );
            await appleAuthService.signOut();
          } catch (error) {
            // Ignorer erreurs
          }
        } else if (isGoogleAuth) {
          try {
            const { googleAuthService } = await import(
              "../services/googleAuthService.js"
            );
            await googleAuthService.signOut();
          } catch (error) {
            // Ignorer erreurs
          }
        } else {
          // ✅ Logout API pour auth classique JSON
          try {
            const storedRefreshToken = SessionStorageService.getRefreshToken();

            // Mode JSON token : utiliser le refresh_token
            await api.post("/auth/logout", {
              refresh_token: storedRefreshToken || refreshToken.value,
              mode: "json",
            });
          } catch (error) {
            // Gestion silencieuse des erreurs de logout API
          }
        }

        // Nettoyer l'état
        clearUser();

        try {
          const { useExtrasStore } = await import("stores/useExtrasStore");
          useExtrasStore().clearPayoutMethods();
        } catch {
          /* silencieux */
        }

        // ✅ Utiliser SessionStorageService.clearAll() pour un nettoyage complet
        SessionStorageService.clearAll();

        // Nettoyer l'API
        delete api.defaults.headers.common["Authorization"];
      } catch (error) {
        // Fallback de nettoyage en cas d'erreur
        clearUser();
        SessionStorageService.clearAll();
        delete api.defaults.headers.common["Authorization"];
      }
    }

    /**
     * Vérification de l'authentification avec cache
     * @param {Object} options - Options de vérification
     * @param {boolean} options.skipUserMe - Skip la vérification users/me
     */
    async function checkAuth({ skipUserMe = false } = {}) {
      // ✅ Utiliser SessionStorageService pour récupérer le token
      const savedToken = SessionStorageService.getToken();
      if (savedToken) {
        try {
          if (!skipUserMe) {
            api.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${savedToken}`;

            const response = await api.get("users/me", {
              params: { fields: USER_FIELDS },
            });

            setAuthData(savedToken, response.data.data);
          } else {
            // Mode skipUserMe : juste configurer le token pour des appels API simples
            token.value = savedToken;
            sessionToken.value = savedToken;
            api.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${savedToken}`;
          }
        } catch (error) {
          await logout();
        }
      }
    }

    /**
     * Initialisation des services en parallèle
     */
    async function initializeServices() {
      const servicesStart = performance.now();

      try {
        const fcmStart = performance.now();
        const realtimeStart = performance.now();

        const [fcmResult, realtimeResult] = await Promise.allSettled([
          // useFCMStore().initialize(),
          useRealtimeStore().initialize(),
        ]);

        const fcmEnd = performance.now();

        // fcmInitialized.value = fcmResult.status === "fulfilled";
        realtimeInitialized.value = realtimeResult.status === "fulfilled";

        // ✅ Associer l'userId au device existant (anonyme → authentifié)
        /* if (fcmInitialized.value && user.value?.id) {
          // const fcmStore = useFCMStore();
          // fcmStore.setUserId(user.value.id);
          // Le device sera mis à jour automatiquement lors de la prochaine sauvegarde du token FCM
          // via saveFCMTokenToDirectus qui utilise déjà l'userId du store */
      } catch (error) {
        // Ignorer les erreurs d'initialisation
      }

      const servicesEnd = performance.now();
    }

    /**
     * Nettoyage des services
     */
    async function cleanupServices() {
      try {
        await Promise.allSettled([
          // useFCMStore().cleanup(),
          useRealtimeStore().disconnect(),
        ]);

        // fcmInitialized.value = false;
        realtimeInitialized.value = false;
      } catch (error) {
        // Erreur lors du nettoyage des services
      }
    }

    /**
     * Mise à jour des informations utilisateur
     * @param {Object} updates - Nouvelles informations
     */
    function updateUser(updates) {
      try {
        if (user.value) {
          user.value = { ...user.value, ...updates };

          // ✅ Utiliser SessionStorageService pour la mise à jour
          SessionStorageService.setUser(user.value);

          // Mettre à jour le cache
          cache.value.user = user.value;
          cache.value.lastFetch = Date.now();
        }
      } catch (error) {
        // Erreur lors de la mise à jour utilisateur
      }
    }

    /**
     * Récupération de l'utilisateur actuel avec cache intelligent.
     * Passe par `api` (ky configuré) pour refresh auto sur 401 — pas le package `ky` nu.
     */
    async function getCurrentUser() {
      if (
        cache.value.user &&
        Date.now() - cache.value.lastFetch < cache.value.ttl
      ) {
        return cache.value.user;
      }

      if (!SessionStorageService.getToken() && user.value) {
        return user.value;
      }

      try {
        const userResponse = await api.get("users/me", {
          params: { fields: USER_FIELDS },
          withCredentials: true,
        });

        const userData = userResponse.data?.data;
        if (!userData) {
          throw new Error("USERS_ME_EMPTY");
        }

        cache.value.user = userData;
        cache.value.lastFetch = Date.now();

        if (JSON.stringify(user.value) !== JSON.stringify(userData)) {
          user.value = userData;
          userRole.value = user.value?.role?.name || null;
          SessionStorageService.setUser(user.value);
        }

        return userData;
      } catch (error) {
        if (user.value) {
          return user.value;
        }
        throw error;
      }
    }

    /**
     * Initialisation depuis le localStorage
     * @param {boolean} initServices - Si true, initialise les services immédiatement (défaut: true)
     */
    function initializeFromStorage(initServices = true) {
      const initStart = performance.now();
      try {
        // ✅ Utiliser SessionStorageService pour récupérer les données
        const savedUser = SessionStorageService.getUser();
        const savedToken = SessionStorageService.getToken();

        if (savedUser && savedToken) {
          const parseStart = performance.now();
          const userData = savedUser; // Déjà parsé par SessionStorageService
          const parseEnd = performance.now();

          // Utiliser la fonction centralisée pour initialiser l'état
          // Ne pas initialiser les services immédiatement au boot pour ne pas bloquer
          setAuthData(savedToken, userData, initServices);
        } else {
          // Pas de données valides, s'assurer que l'utilisateur est déconnecté
          clearUser();
        }
      } catch (error) {
        clearUser();
      }
      const initEnd = performance.now();
    }

    /**
     * Nettoyage complet des données utilisateur
     */
    /**
     * Après /auth/refresh : met à jour session + store sans déconnecter l'utilisateur.
     */
    function applyRefreshedTokens(accessToken, newRefreshToken = null) {
      if (!accessToken) return;

      token.value = accessToken;
      sessionToken.value = accessToken;
      isAuthenticated.value = true;

      api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      SessionStorageService.setToken(accessToken);

      if (newRefreshToken) {
        refreshToken.value = newRefreshToken;
        SessionStorageService.setRefreshToken(newRefreshToken);
      }
    }

    function clearUser() {
      user.value = null;
      token.value = null;
      refreshToken.value = null;
      isAuthenticated.value = false;
      userRole.value = null;
      lastLogin.value = null;
      sessionToken.value = null;
      cache.value = { user: null, lastFetch: 0, ttl: 30000 };
      try {
        analyticsService.clearUser();
      } catch {
        /* silencieux */
      }
    }

    /**
     * Vérification de la validité de la session
     */
    function checkSessionValidity() {
      if (!hasValidSession.value) {
        return false;
      }

      // ✅ Utiliser SessionStorageService pour vérifier le token
      const currentToken = SessionStorageService.getToken();
      if (!currentToken) {
        logout();
        return false;
      }

      // Comparer avec le token de session
      if (currentToken !== sessionToken.value) {
        logout();
        return false;
      }

      return true;
    }

    /**
     * Acceptation des CGU
     * Supporte à la fois le mode TOKEN et le mode COOKIE
     */
    async function acceptTerms() {
      if (!user.value?.id) {
        await refreshUser();
      }
      if (!user.value?.id) {
        throw new Error("Aucun utilisateur connecté");
      }

      try {
        // Déterminer si on est en mode cookie (OAuth sans token)
        const isCookieAuth = LocalStorage.getItem("useCookieAuth");
        const hasToken = token.value || SessionStorageService.getToken();

        const updateData = {
          accepted_terms: true,
          first_authentificate: false,
        };

        // ✅ Si mode cookie, utiliser withCredentials pour envoyer les cookies
        if (isCookieAuth && !hasToken) {
          await api.patch(`users/${user.value.id}`, updateData, {
            withCredentials: true,
          });
        } else {
          // Mode TOKEN : Utiliser api normalement (token ajouté automatiquement)
          await api.patch(`users/${user.value.id}`, updateData);
        }

        await refreshUser();

        if (user.value) {
          LocalStorage.setItem("ykSessionUser", JSON.stringify(user.value));
          SessionStorageService.setUser(user.value);
          cache.value.user = user.value;
          cache.value.lastFetch = Date.now();
        }

        return user.value;
      } catch (error) {
        throw error;
      }
    }

    /**
     * Vérification de l'acceptation des CGU
     */
    function hasAcceptedTerms() {
      if (!isAuthenticated.value) {
        return user.value?.accepted_terms === true;
      }
      return true;
    }

    /**
     * Vérification de la première authentification
     */
    function isFirstAuthentication() {
      return user.value?.first_authentificate === true;
    }

    /**
     * Vérification si les CGU doivent être affichées
     */
    function shouldShowTerms() {
      return isFirstAuthentication() && !hasAcceptedTerms();
    }

    /**
     * Mise à jour du rôle utilisateur
     * Supporte à la fois le mode TOKEN et le mode COOKIE
     * @param {string|Object} roleId - ID du rôle ou objet rôle
     */
    async function updateUserRole(roleId) {
      if (!user.value?.id) {
        throw new Error("Aucun utilisateur connecté");
      }

      try {
        // Déterminer si on est en mode cookie (OAuth sans token)
        const isCookieAuth = LocalStorage.getItem("useCookieAuth");
        const hasToken = token.value || SessionStorageService.getToken();

        // ✅ Si mode cookie, utiliser withCredentials pour envoyer les cookies
        const requestConfig = {
          role: roleId?.id || roleId,
          first_authentificate: false,
        };

        if (isCookieAuth && !hasToken) {
          // Mode COOKIE : Utiliser api avec withCredentials
          await api.patch(`users/${user.value.id}`, requestConfig, {
            withCredentials: true,
          });
        } else {
          // Mode TOKEN : Utiliser api normalement (token ajouté automatiquement)
          await api.patch(`users/${user.value.id}`, requestConfig);
        }

        await refreshUser();

        if (user.value) {
          userRole.value = user.value?.role?.name || null;
          LocalStorage.setItem("ykSessionUser", JSON.stringify(user.value));
          SessionStorageService.setUser(user.value);
          cache.value.user = user.value;
          cache.value.lastFetch = Date.now();
        }

        return user.value;
      } catch (error) {
        throw error;
      }
    }

    /**
     * Rafraîchissement des données utilisateur depuis l'API
     * ✅ Utilise kyApi qui gère automatiquement le token et le refresh
     */
    async function refreshUser() {
      // ✅ Vérifier si l'utilisateur est authentifié (token OU cookies)
      const isCookieAuth = LocalStorage.getItem("useCookieAuth");
      const hasToken = token.value || SessionStorageService.getToken();
      const isAuth = isAuthenticated.value || user.value;

      // Si ni token ni authentification, ne rien faire
      if (!hasToken && !isCookieAuth && !isAuth) return;

      try {
        let userData;

        // ✅ MODE COOKIE : Utiliser ky avec credentials pour les cookies
        if (isCookieAuth && !hasToken) {
          // Créer une instance ky avec credentials pour les cookies
          const kyClient = ky.create({
            prefixUrl: backAdresse,
            credentials: "include", // ✅ Inclure les cookies automatiquement
            timeout: 30000,
          });

          const userResponse = await kyClient
            .get(`users/me?fields=${USER_FIELDS}`)
            .json();

          userData = userResponse.data;
        } else {
          const userResponse = await api.get("users/me", {
            params: { fields: USER_FIELDS },
          });
          userData = userResponse.data.data;
        }

        user.value = userData;
        userRole.value = user.value?.role?.name || null;

        // ✅ Utiliser SessionStorageService pour la mise à jour
        SessionStorageService.setUser(user.value);

        // Mettre à jour le cache
        cache.value.user = user.value;
        cache.value.lastFetch = Date.now();

        return user.value;
      } catch (error) {
        // console.error("❌ [authUnified] refreshUser error:", error);
        throw error;
      }
    }

    /**
     * Rafraîchissement des données depuis le localStorage
     * Retourne une promesse pour la compatibilité avec le code existant
     */
    function refreshUserData() {
      initializeFromStorage();
      return Promise.resolve();
    }

    /**
     * Définit le statut hôte (fonction de compatibilité)
     * @param {string} status - Statut hôte
     */
    function setHostStatus(status) {
      // Cette fonction est maintenue pour la compatibilité
      // Le statut hôte est maintenant géré via le computed isHost
    }

    /**
     * 🔐 MODE COOKIE OAUTH UNIQUEMENT
     * Initialise la session depuis les cookies OAuth (après redirection Directus)
     *
     * ⚠️ ATTENTION : Cette fonction est UNIQUEMENT pour l'authentification OAuth (Google, Apple)
     * car Directus retourne des cookies HTTP-only après le redirect OAuth.
     *
     * Pour le login classique email/password, utilisez login() qui utilise le mode JSON.
     *
     * @returns {Promise<{user: Object}>}
     */
    async function initializeFromOAuthCookie() {
      try {
        isLoading.value = true;

        // 🍪 MODE COOKIE OAUTH avec récupération de token pour WebSocket

        // Créer un client Directus temporaire pour récupérer le token
        const {
          createDirectus,
          authentication: directusAuth,
          rest: directusRest,
        } = await import("@directus/sdk");

        const tempClient = createDirectus(backAdresse)
          .with(
            directusAuth("cookie", {
              credentials: "include",
              autoRefresh: true,
            })
          )
          .with(directusRest());

        // 🔑 RÉCUPÉRER LE TOKEN depuis les cookies avec getToken()
        let accessToken = null;
        let refreshTokenValue = null;

        try {
          // ÉTAPE 1 : Faire un refresh() pour que le SDK charge les cookies et récupère le token
          await tempClient.refresh();

          // ÉTAPE 2 : Essayer de récupérer le token depuis le client
          accessToken = await tempClient.getToken();
        } catch (refreshError) {

          // FALLBACK : Essayer de récupérer le token via une requête directe
          try {
            const refreshResponse = await ky
              .post(`${backAdresse}auth/refresh`, {
                json: { mode: "cookie" },
                credentials: "include",
              })
              .json();

            if (refreshResponse?.data?.access_token) {
              accessToken = refreshResponse.data.access_token;
              refreshTokenValue = refreshResponse.data.refresh_token; // ✅ Récupérer aussi le refresh_token
            }
          } catch (fallbackError) {
          }
        }

        // Créer une instance ky avec credentials pour les cookies
        const kyClient = ky.create({
          prefixUrl: backAdresse,
          credentials: "include", // ✅ Inclure les cookies automatiquement
          timeout: 30000,
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {},
        });

        // Récupérer les données utilisateur
        const userResponse = await kyClient
          .get(`users/me?fields=${USER_FIELDS}`)
          .json();

        const userData = userResponse.data;

        if (accessToken) {
          // ✅ MODE TOKEN : On a récupéré un token, l'utiliser pour tout

          LocalStorage.remove("useCookieAuth");

          setAuthData(accessToken, userData);
          await synchroniserMoyensPaiementRetrait(true);

          if (refreshTokenValue) {
            refreshToken.value = refreshTokenValue;
            SessionStorageService.setRefreshToken(refreshTokenValue);
          } else {
          }
        } else {
          // ⚠️ FALLBACK : Pas de token, rester en mode cookie (WebSocket désactivé)
          LocalStorage.setItem("useCookieAuth", true);

          user.value = userData;
          isAuthenticated.value = true;
          userRole.value = userData?.role?.name || null;
          lastLogin.value = new Date().toISOString();
          SessionStorageService.setUser(userData);
          cache.value.user = userData;
          cache.value.lastFetch = Date.now();

          // Ne PAS ajouter de header Authorization - on utilise les cookies
          delete api.defaults.headers.common["Authorization"];

          // Initialiser les services (WebSocket sera désactivé)
          setTimeout(() => {
            initializeServices();
          }, 0);
        }

        return { user: userData, token: accessToken };
      } catch (error) {
        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    return {
      // State
      user,
      token,
      refreshToken,
      isAuthenticated,
      isLoading,
      userRole,
      lastLogin,
      sessionToken,
      cache,
      fcmInitialized,
      realtimeInitialized,
      navigator,

      // Getters
      isHost,
      isVoyageur,
      isDemarcheur,
      userFullName,
      userEmail,
      userPhone,
      userAvatar,
      hasValidSession,

      // Actions principales
      login,
      socialLogin,
      logout,
      checkAuth,
      setAuthData, // Exposer la nouvelle fonction
      applyRefreshedTokens,
      initializeFromOAuthCookie, // ✅ Initialise la session depuis les cookies OAuth
      initializeServices,
      cleanupServices,
      updateUser,
      getCurrentUser,
      initializeFromStorage,
      clearUser,
      checkSessionValidity,
      refreshUserData,
      setHostStatus,

      // Actions CGU et rôles
      acceptTerms,
      hasAcceptedTerms,
      isFirstAuthentication,
      shouldShowTerms,
      updateUserRole,
      refreshUser,
    };
  },
  {
    // 💾 Configuration de persistance pour les données utilisateur
    persist: {
      key: "awlo-auth-unified-store",
      storage: ssrSafeLocalStorage,
      paths: [
        "user",
        "token",
        "refreshToken",
        "isAuthenticated",
        "userRole",
        "lastLogin",
        "sessionToken",
      ],
      // Fonction pour s'assurer que les structures sont correctement initialisées après hydratation
      afterRestore: (ctx) => {
        // Réinitialiser le cache qui ne doit pas être persisté
        if (!ctx.store.cache || typeof ctx.store.cache !== "object") {
          ctx.store.cache = {
            user: null,
            lastFetch: 0,
            ttl: 30000,
          };
        }
        // Réinitialiser fcmInitialized qui ne doit pas être persisté
        if (typeof ctx.store.fcmInitialized !== "boolean") {
          ctx.store.fcmInitialized = false;
        }
        // Réinitialiser realtimeInitialized qui ne doit pas être persisté
        if (typeof ctx.store.realtimeInitialized !== "boolean") {
          ctx.store.realtimeInitialized = false;
        }
      },
    },
  }
);
